import { createContext, useContext, type ReactNode } from 'react'

import type { IPlatformIdentityServices } from './types'
import { setPlatformServices } from './platform-services'

const PlatformContext = createContext<IPlatformIdentityServices | null>(null)

interface Props<T extends IPlatformIdentityServices> {
  children: ReactNode
  services: T
}

/**
 * Provides platform services to the React tree and simultaneously registers them
 * at module level so non-React code (Redux listeners) can access the same
 * instance via getPlatformServices().
 */
export function PlatformProvider<T extends IPlatformIdentityServices>({
  children,
  services
}: Props<T>) {
  setPlatformServices(services)

  return <PlatformContext.Provider value={services}>{children}</PlatformContext.Provider>
}

export function usePlatform<T extends IPlatformIdentityServices = IPlatformIdentityServices>(): T {
  const ctx = useContext(PlatformContext) as T | null
  if (!ctx) {
    throw new Error('usePlatform must be used within a <PlatformProvider>')
  }
  return ctx
}
